import { useMemo, useState } from "react";
import { FaChevronDown, FaChevronUp, FaSearch, FaTimes } from "react-icons/fa";
import AppIcon from "../AppIcon";
import { useProfileEditorAppCatalog } from "../../hooks/useProfileEditorAppCatalog";
import OsVersionBadges from "./OsVersionBadges";

const DEFAULT_APP_SETTINGS = {
  NotificationsEnabled: true,
  ShowInNotificationCenter: true,
  ShowInLockScreen: true,
  BadgesEnabled: true,
  SoundsEnabled: true,
  ShowInCarPlay: true,
  CriticalAlertEnabled: false,
  AlertType: 1,
  GroupingType: 0,
};

function asSettings(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export default function NotificationsEditor({ fields, onChange, disabled }) {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState("");
  const settings = asSettings(fields?.NotificationSettings);
  const extraBundleIds = useMemo(
    () => settings.map((row) => row.BundleIdentifier).filter(Boolean),
    [settings]
  );
  const { appsById: appsByIdMap } = useProfileEditorAppCatalog(extraBundleIds);

  const catalog = useMemo(() => {
    const map = new Map(appsByIdMap);
    return map;
  }, [appsByIdMap]);

  const searchHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [...catalog.entries()].slice(0, 24).map(([bundleId, meta]) => ({ bundleId, ...meta }));
    return [...catalog.entries()]
      .filter(([bundleId, meta]) => meta.name.toLowerCase().includes(q) || bundleId.toLowerCase().includes(q))
      .slice(0, 24)
      .map(([bundleId, meta]) => ({ bundleId, ...meta }));
  }, [catalog, query]);

  const commit = (next) => onChange({ ...fields, NotificationSettings: next });

  const addApp = (bundleId) => {
    if (disabled || settings.some((row) => row.BundleIdentifier === bundleId)) return;
    const next = [...settings, { BundleIdentifier: bundleId, ...DEFAULT_APP_SETTINGS }];
    commit(next);
    setOpenId(bundleId);
    setQuery("");
  };

  const updateApp = (bundleId, patch) => {
    commit(settings.map((row) => (row.BundleIdentifier === bundleId ? { ...row, ...patch } : row)));
  };

  return (
    <div>
      <div className="mb-2">
        <label className="form-label mb-1">Vorschau anzeigen</label>
        <div className="d-flex align-items-center gap-2">
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 240 }}
            disabled={disabled}
            value={fields?.PreviewType === undefined || fields?.PreviewType === "" ? "" : String(fields.PreviewType)}
            onChange={(e) => {
              const preview = e.target.value === "" ? undefined : Number(e.target.value);
              onChange({
                ...fields,
                PreviewType: preview,
                NotificationSettings: settings.map((row) => ({ ...row, PreviewType: preview })),
              });
            }}
          >
            <option value="">Nicht konfigurieren</option>
            <option value="0">Immer</option>
            <option value="1">Bei entsperrtem Gerät</option>
            <option value="2">Nie</option>
          </select>
          <OsVersionBadges introduced={{ iOS: "14.0" }} />
        </div>
        <div className="form-text">Globale Vorschau (Settings → Mitteilungen). Pro App unten überschreibbar.</div>
      </div>

      <label className="form-label">Anwendungen auswählen</label>
      <div className="d-flex flex-wrap gap-1 mb-2">
        {settings.map((row) => {
          const meta = catalog.get(row.BundleIdentifier);
          return (
            <span key={row.BundleIdentifier} className="badge text-bg-light text-dark border d-inline-flex align-items-center gap-1">
              {meta?.name || row.BundleIdentifier}
              <button
                type="button"
                className="btn p-0 border-0 bg-transparent"
                disabled={disabled}
                aria-label={`${meta?.name || row.BundleIdentifier} entfernen`}
                onClick={() => commit(settings.filter((item) => item.BundleIdentifier !== row.BundleIdentifier))}
              >
                <FaTimes />
              </button>
            </span>
          );
        })}
      </div>
      <div className="input-group input-group-sm mb-2">
        <span className="input-group-text"><FaSearch /></span>
        <input
          className="form-control"
          placeholder="App suchen…"
          disabled={disabled}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {query.trim() ? (
        <div className="d-flex flex-wrap gap-1 mb-3">
          {searchHits.map((app) => (
            <button
              key={app.bundleId}
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={disabled || settings.some((row) => row.BundleIdentifier === app.bundleId)}
              onClick={() => addApp(app.bundleId)}
            >
              {app.name}
            </button>
          ))}
        </div>
      ) : (
        <div className="form-text mb-3">Verwaltete oder eingebaute App suchen und zur Auswahl hinzufügen.</div>
      )}

      <div className="form-label">Ausgewählte Anwendungen</div>
      {settings.map((row) => {
        const meta = catalog.get(row.BundleIdentifier) || { name: row.BundleIdentifier, artworkUrl: "" };
        const open = openId === row.BundleIdentifier;
        return (
          <div className="notif-app-panel" key={row.BundleIdentifier}>
            <button type="button" className="notif-app-header" onClick={() => setOpenId(open ? "" : row.BundleIdentifier)}>
              <AppIcon src={meta.artworkUrl} name={meta.name} size={28} />
              <span className="flex-grow-1">{meta.name}</span>
              {open ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {open ? (
              <div>
                <div className="notif-app-grid">
                  {[
                    ["NotificationsEnabled", "Mitteilungen erlauben"],
                    ["ShowInLockScreen", "Auf Sperrbildschirm anzeigen"],
                    ["BadgesEnabled", "Kennzeichen für App-Symbol"],
                    ["ShowInNotificationCenter", "In Mitteilungszentrale anzeigen"],
                    ["SoundsEnabled", "Töne"],
                    ["ShowInCarPlay", "In CarPlay anzeigen", { iOS: "12.0" }],
                    ["CriticalAlertEnabled", "Kritische Warnmeldungen", { iOS: "12.0" }],
                  ].map(([key, label, introduced]) => (
                    <label key={key} className="form-check mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        disabled={disabled}
                        checked={key === "CriticalAlertEnabled" ? Boolean(row[key]) : row[key] !== false}
                        onChange={(e) => updateApp(row.BundleIdentifier, { [key]: e.target.checked })}
                      />
                      <span className="form-check-label d-inline-flex align-items-center gap-1">
                        {label}
                        {introduced ? <OsVersionBadges introduced={introduced} /> : null}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="row g-2 px-3 pb-3">
                  <div className="col-md-6">
                    <label className="form-label mb-0">Hinweisstil beim Entsperren</label>
                    <select
                      className="form-select form-select-sm"
                      disabled={disabled}
                      value={row.AlertType ?? 1}
                      onChange={(e) => updateApp(row.BundleIdentifier, { AlertType: Number(e.target.value) })}
                    >
                      <option value={0}>Keine</option>
                      <option value={1}>Temporär</option>
                      <option value={2}>Dauerhaft</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label mb-0 d-inline-flex align-items-center gap-1">
                      Mitteilungsgruppierung <OsVersionBadges introduced={{ iOS: "12.0" }} />
                    </label>
                    <select
                      className="form-select form-select-sm"
                      disabled={disabled}
                      value={row.GroupingType ?? 0}
                      onChange={(e) => updateApp(row.BundleIdentifier, { GroupingType: Number(e.target.value) })}
                    >
                      <option value={0}>Automatisch</option>
                      <option value={1}>Nach App</option>
                      <option value={2}>Aus</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label mb-0 d-inline-flex align-items-center gap-1">
                      Vorschau <OsVersionBadges introduced={{ iOS: "14.0" }} />
                    </label>
                    <select
                      className="form-select form-select-sm"
                      disabled={disabled}
                      value={row.PreviewType === undefined || row.PreviewType === "" ? "" : String(row.PreviewType)}
                      onChange={(e) => updateApp(row.BundleIdentifier, {
                        PreviewType: e.target.value === "" ? undefined : Number(e.target.value),
                      })}
                    >
                      <option value="">Nicht konfigurieren</option>
                      <option value="0">Immer</option>
                      <option value="1">Bei entsperrtem Gerät</option>
                      <option value="2">Nie</option>
                    </select>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
